import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { AlertTriangle, FileDown, Loader2, PenLine, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";

export const WARNING_TYPE_LABEL = { falta: "Falta injustificada", atraso: "Atraso" };

export async function downloadWarningPdf(w) {
  try {
    const res = await api.get(`/timeclock/warnings/${w.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `advertencia-${(w.user_name || "colaborador").replace(/\s+/g, "_")}-${w.month}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Falha ao gerar PDF da advertência");
  }
}

// Diálogo de assinatura de ciência
export function WarningSignDialog({ warning, open, onOpenChange, onSigned }) {
  const [signature, setSignature] = useState(null);
  const [busy, setBusy] = useState(false);

  const sign = async () => {
    if (!signature) {
      toast.error("Assine no quadro para registrar a ciência");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/timeclock/warnings/${warning.id}/sign`, {
        signature_png: signature,
        signer_name: warning.user_name,
      });
      toast.success("Ciência registrada com sucesso");
      onOpenChange(false);
      onSigned?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao registrar assinatura");
    } finally {
      setBusy(false);
    }
  };

  if (!warning) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="warning-sign-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <PenLine className="h-5 w-5 text-emerald-700" /> Ciência da advertência
          </DialogTitle>
          <DialogDescription className="font-body text-xs text-stone-500">
            {warning.user_name} · {WARNING_TYPE_LABEL[warning.warning_type] || warning.warning_type} ·{" "}
            {String(warning.month).split("-").reverse().join("/")}. A assinatura registra apenas a
            ciência, não implicando concordância.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-stone-200 bg-stone-50 p-4 max-h-56 overflow-y-auto text-sm font-body whitespace-pre-wrap leading-relaxed">
          {warning.text}
        </div>
        <div>
          <Label className="text-xs">Assinatura do colaborador</Label>
          <div className="mt-1">
            <SignaturePad onChange={setSignature} />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={sign}
            disabled={busy}
            className="bg-emerald-800 hover:bg-emerald-900 text-white"
            data-testid="warning-sign-save"
          >
            {busy ? "Registrando…" : "Registrar ciência"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Diálogo de criação (com texto gerado por IA)
export function WarningCreateDialog({ open, onOpenChange, userId, userName, month, defaultDetails, onCreated }) {
  const [warningType, setWarningType] = useState("falta");
  const [details, setDetails] = useState("");
  const [text, setText] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDetails(defaultDetails || "");
      setText("");
      setWarningType("falta");
    }
  }, [open, defaultDetails]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/timeclock/warnings/generate", {
        user_id: userId,
        month,
        warning_type: warningType,
        details,
      });
      setText(data.text);
      toast.success("Texto gerado pela IA — revise antes de salvar");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao gerar texto com IA");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!text.trim()) {
      toast.error("Gere ou escreva o texto da advertência");
      return;
    }
    setSaving(true);
    try {
      await api.post("/timeclock/warnings", {
        user_id: userId,
        month,
        warning_type: warningType,
        text,
        details,
      });
      toast.success("Advertência criada e arquivada — pendente de assinatura");
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar advertência");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="warning-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-rose-600" /> Gerar advertência — {userName}
          </DialogTitle>
          <DialogDescription className="font-body text-xs text-stone-500">
            Período {String(month).split("-").reverse().join("/")}. A IA redige o texto formal
            (CLT); revise e salve. Depois, colha a assinatura de ciência do colaborador.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Motivo</Label>
              <Select value={warningType} onValueChange={setWarningType}>
                <SelectTrigger className="mt-1 bg-white" data-testid="warning-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="falta">Falta injustificada</SelectItem>
                  <SelectItem value="atraso">Atraso</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Detalhes da apuração (usados pela IA)</Label>
            <Textarea
              rows={2}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              className="mt-1 bg-white text-xs"
              data-testid="warning-details-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={generate}
            disabled={generating}
            className="border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100"
            data-testid="warning-generate-ai"
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Gerando com IA…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" /> Gerar texto com IA
              </>
            )}
          </Button>
          <div>
            <Label className="text-xs">Texto da advertência</Label>
            <Textarea
              rows={10}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Clique em 'Gerar texto com IA' ou escreva manualmente…"
              className="mt-1 bg-white text-sm leading-relaxed"
              data-testid="warning-text-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={save}
            disabled={saving || !text.trim()}
            className="bg-rose-700 hover:bg-rose-800 text-white"
            data-testid="warning-save-btn"
          >
            {saving ? "Salvando…" : "Salvar advertência"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Popup de visualização rápida da advertência
export function WarningViewDialog({ warning, open, onOpenChange, onCollectSign }) {
  if (!warning) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="warning-view-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-rose-600" /> Advertência — {warning.user_name}
          </DialogTitle>
          <DialogDescription className="font-body text-xs text-stone-500">
            Emitida por {warning.created_by_name} · Período{" "}
            {String(warning.month).split("-").reverse().join("/")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className={
              warning.warning_type === "falta"
                ? "bg-rose-50 text-rose-800 border-rose-200 text-[10px]"
                : "bg-amber-50 text-amber-800 border-amber-200 text-[10px]"
            }
          >
            {WARNING_TYPE_LABEL[warning.warning_type] || warning.warning_type}
          </Badge>
          {warning.status === "signed" ? (
            <Badge className="bg-emerald-600 text-white border-none text-[10px]">
              Ciência assinada
            </Badge>
          ) : (
            <Badge className="bg-amber-500 text-white border-none text-[10px]">
              Assinatura pendente
            </Badge>
          )}
        </div>
        {warning.details && (
          <div className="rounded-lg border border-stone-200 bg-white p-3 text-xs font-body text-stone-500">
            <span className="font-semibold text-stone-700">Detalhes da apuração: </span>
            {warning.details}
          </div>
        )}
        <div
          className="rounded-lg border border-stone-200 bg-stone-50 p-4 max-h-[45vh] overflow-y-auto text-sm font-body whitespace-pre-wrap leading-relaxed"
          data-testid="warning-view-text"
        >
          {warning.text}
        </div>
        <DialogFooter className="gap-2">
          {warning.status !== "signed" && onCollectSign && (
            <Button
              variant="outline"
              className="border-emerald-300 text-emerald-800 bg-white"
              onClick={() => {
                onOpenChange(false);
                onCollectSign(warning);
              }}
              data-testid="warning-view-collect-sign"
            >
              <PenLine className="h-4 w-4 mr-1.5" /> Colher assinatura
            </Button>
          )}
          <Button
            className="bg-emerald-800 hover:bg-emerald-900 text-white"
            onClick={() => downloadWarningPdf(warning)}
            data-testid="warning-view-pdf"
          >
            <FileDown className="h-4 w-4 mr-1.5" /> Baixar PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Aba do admin — arquivo de advertências da empresa
export default function WarningsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/timeclock/warnings");
      setItems(data);
    } catch (err) {
      console.error("warnings load failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-3" data-testid="warnings-tab">
      <p className="text-xs text-stone-500 font-body">
        Advertências disciplinares geradas na apuração de ponto, arquivadas com assinatura de
        ciência — histórico disciplinar do colaborador (prova documental).
      </p>
      <Card className="rounded-xl border-stone-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-body">Colaborador</TableHead>
              <TableHead className="font-body">Mês</TableHead>
              <TableHead className="font-body">Motivo</TableHead>
              <TableHead className="font-body">Emitida por</TableHead>
              <TableHead className="font-body">Ciência</TableHead>
              <TableHead className="font-body text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-stone-400">
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-stone-500 font-body text-sm">
                  Nenhuma advertência registrada.
                </TableCell>
              </TableRow>
            )}
            {items.map((w) => (
              <TableRow
                key={w.id}
                data-testid={`warning-row-${w.id}`}
                className="cursor-pointer hover:bg-stone-50"
                onClick={() => setViewing(w)}
              >
                <TableCell className="font-display font-semibold text-sm">{w.user_name}</TableCell>
                <TableCell className="font-body text-sm tabular-nums">
                  {String(w.month).split("-").reverse().join("/")}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={
                      w.warning_type === "falta"
                        ? "bg-rose-50 text-rose-800 border-rose-200 text-[10px]"
                        : "bg-amber-50 text-amber-800 border-amber-200 text-[10px]"
                    }
                  >
                    {WARNING_TYPE_LABEL[w.warning_type] || w.warning_type}
                  </Badge>
                </TableCell>
                <TableCell className="font-body text-xs text-stone-500">{w.created_by_name}</TableCell>
                <TableCell>
                  {w.status === "signed" ? (
                    <Badge className="bg-emerald-600 text-white border-none text-[10px]">Assinada</Badge>
                  ) : (
                    <Badge className="bg-amber-500 text-white border-none text-[10px]">Pendente</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1.5">
                    {w.status !== "signed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-300 text-emerald-800 bg-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSigning(w);
                        }}
                        data-testid={`warning-collect-sign-${w.id}`}
                      >
                        <PenLine className="h-3.5 w-3.5 mr-1" /> Colher assinatura
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadWarningPdf(w);
                      }}
                      data-testid={`warning-pdf-${w.id}`}
                    >
                      <FileDown className="h-3.5 w-3.5 mr-1" /> PDF
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <WarningViewDialog
        warning={viewing}
        open={!!viewing}
        onOpenChange={(o) => !o && setViewing(null)}
        onCollectSign={(w) => setSigning(w)}
      />
      <WarningSignDialog
        warning={signing}
        open={!!signing}
        onOpenChange={(o) => !o && setSigning(null)}
        onSigned={load}
      />
    </div>
  );
}
